import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Search, ExternalLink, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import SyncBar from '../components/SyncBar';
import PetanqueLoader from '../components/PetanqueLoader';
import InvoiceDetailDialog from '../components/InvoiceDetailDialog';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { api } from '../api/sangly';
import { money, moneyExact, fmtDate, PAYMENT } from '../lib/format';
import { cn } from '../lib/utils';
import usePollWhileActive from '../hooks/usePollWhileActive';

const PAGE = 50;

// A retrocommission is a Self Billing bill the B2B portal's automation books in
// Odoo for a partner (ref RETRO-…). It stays a draft until accounting confirms
// it, is paid by bank transfer from Odoo, and Sangly shows it paid after the sync.
const STATUS_FILTERS = [
  { key: 'pending', label: 'To do' },   // to confirm (draft) + to pay (posted, not paid) · the default
  { key: 'draft', label: 'To confirm' },
  { key: 'open', label: 'To pay' },
  { key: 'paid', label: 'Paid' },
  { key: 'all', label: 'All' },
];

export default function Retrocommissions() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState(searchParams.get('status') || 'pending');
  const [ordering, setOrdering] = useState('-due');
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);

  const params = useMemo(() => {
    const p = { retro: 'true', ordering, limit: PAGE, offset };
    if (q.trim()) p.q = q.trim();
    if (status !== 'all') p.status = status;
    return p;
  }, [q, status, ordering, offset]);

  const load = useCallback(async (soft = false) => {
    if (!soft) setLoading(true);
    try {
      const d = await api.invoices.list(params);
      setRows(d.results || []);
      setCount(d.count || 0);
    } finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.users.list().then(setUsers).catch(() => {}); }, []);
  useEffect(() => { setOffset(0); }, [q, status, ordering]);
  usePollWhileActive(true, () => load(true), 20000);

  const from = count === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE, count);

  return (
    <AppLayout
      title="Retrocommissions"
      description="Commissions paid back to B2B partners — the Self Billing bills (RETRO-…) the portal books in Odoo, from draft to paid"
      syncBar={<SyncBar onSynced={() => load(true)} />}
    >
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex flex-wrap gap-1.5">
          {STATUS_FILTERS.map((f) => (
            <button key={f.key} onClick={() => setStatus(f.key)}
              className={cn('rounded-md border px-2.5 py-1 text-[12px] font-medium transition-colors',
                status === f.key ? 'border-primary bg-primary/12 text-foreground' : 'border-border bg-card text-muted-foreground hover:bg-muted')}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Reference, partner, bill #…"
                 className="h-9 w-full pl-8 sm:w-64" />
        </div>
        <Select className="h-9 w-full sm:ml-auto sm:w-44" value={ordering} onChange={(e) => setOrdering(e.target.value)}>
          <option value="-due">Date ↓</option>
          <option value="due">Date ↑</option>
          <option value="-invoice_amount">Amount ↓</option>
          <option value="invoice_amount">Amount ↑</option>
          <option value="-ref">Reference ↓</option>
          <option value="supplier">Partner A–Z</option>
        </Select>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card shadow-card">
        <Table className="min-w-[900px]">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Reference</TableHead>
              <TableHead>Partner</TableHead>
              <TableHead>Bill</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Due</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Links</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={8} className="h-40 text-center">
                <PetanqueLoader size={34} roll className="mx-auto" />
              </TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={8} className="h-40 text-center text-muted-foreground">
                {q.trim() ? 'No retrocommissions match your search.' : status === 'pending' ? 'Nothing to do · every retrocommission is confirmed and paid.' : 'No retrocommissions here.'}
              </TableCell></TableRow>
            )}
            {!loading && rows.map((inv) => (
              <TableRow key={inv.id} className="cursor-pointer" onClick={() => navigate(`/retrocommissions/${inv.id}`)}>
                <TableCell className="font-mono font-medium">{inv.ref || '-'}</TableCell>
                <TableCell className="font-medium">{inv.supplier || '-'}</TableCell>
                <TableCell className="font-mono text-[12px] text-muted-foreground">
                  {inv.payment_state === 'draft' ? <span className="font-sans italic">not confirmed</span> : (inv.name || '-')}
                </TableCell>
                <TableCell className="text-muted-foreground">{fmtDate(inv.invoice_date)}</TableCell>
                <TableCell className="text-muted-foreground">{inv.payment_state === 'draft' ? '-' : fmtDate(inv.date_due)}</TableCell>
                <TableCell className="text-right font-semibold tnum whitespace-nowrap" title={moneyExact(inv.amount_total, inv.currency)}>
                  {money(inv.amount_total, inv.currency)}
                  {inv.payment_state === 'partial' && (
                    <div className="text-[11px] font-normal text-muted-foreground">left {money(inv.amount_residual, inv.currency)}</div>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={(PAYMENT[inv.payment_state] || PAYMENT.not_paid).variant}>
                    {inv.payment_state === 'draft' ? 'To confirm' : (PAYMENT[inv.payment_state] || PAYMENT.not_paid).label}
                  </Badge>
                  {inv.payment_date && (inv.payment_state === 'in_payment' || inv.payment_state === 'paid') && (
                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                      {inv.payment_state === 'in_payment' ? 'planned ' : 'paid '}{fmtDate(inv.payment_date)}
                    </div>
                  )}
                  {inv.payment_state === 'draft' && (
                    <div className="mt-0.5 text-[11px] text-muted-foreground">waiting for accounting to confirm it in Odoo</div>
                  )}
                  {inv.payment_state === 'not_paid' && (
                    <div className="mt-0.5 text-[11px] text-muted-foreground">to pay by bank transfer from Odoo</div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2 text-muted-foreground">
                    {inv.pdf_url && (
                      <a href={inv.pdf_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                         className="rounded p-0.5 transition-colors hover:bg-muted hover:text-foreground" title="Open the bill PDF">
                        <FileText className="h-3.5 w-3.5" />
                      </a>
                    )}
                    {inv.odoo_url && (
                      <a href={inv.odoo_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                         className="rounded p-0.5 transition-colors hover:bg-muted hover:text-foreground" title="Open in Odoo">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="mt-3 flex items-center justify-between text-[12px] text-muted-foreground">
        <span>{from}–{to} of {count.toLocaleString('nl-BE')}</span>
        <div className="flex items-center gap-1">
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}
            className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-card px-2 disabled:opacity-40 hover:bg-muted">
            <ChevronLeft className="h-4 w-4" /> Prev
          </button>
          <button disabled={to >= count} onClick={() => setOffset(offset + PAGE)}
            className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-card px-2 disabled:opacity-40 hover:bg-muted">
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <InvoiceDetailDialog invoiceId={id} open={!!id} onClose={() => navigate('/retrocommissions')}
                           onChanged={() => load(true)} users={users} />
    </AppLayout>
  );
}
